/**
 * LivenessStep — wraps the lazy-loaded LivenessCapture component as a
 * profile-complete wizard step.
 *
 * On mount it asks the backend whether the user has already passed the
 * step (re-entry case — the wizard is sticky-stepful but this step is
 * once-per-lifetime). If yes → render the "verified" state with no
 * camera. If not → render the capture flow.
 *
 * Static helpers `runValidations` + `hasErrors` mirror the contract used
 * by the other step components in this folder so ProfileComplete.js can
 * call them uniformly during cross-step validation.
 */

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';

import livenessService from '../../services/livenessService';

// Lazy-loaded — keeps TF.js + MediaPipe (~4.5 MB JS + ~3 MB model) out of
// the main bundle until the user actually reaches this step.
const LivenessCapture = lazy(() => import('../liveness/LivenessCapture'));

const LivenessStep = ({ value, onChange, saving, onError }) => {
    const [statusLoading, setStatusLoading] = useState(true);
    const [serverError, setServerError]     = useState(null);
    const [submitting, setSubmitting]       = useState(false);

    // Pull the verified flag from parent state (ProfileComplete.js owns it).
    const verified   = !!value?.verified;
    const verifiedAt = value?.verified_at || null;
    const imageUrl   = value?.image_url || null;
    const lat        = value?.lat ?? null;
    const lng        = value?.lng ?? null;
    const accuracyM  = value?.accuracy_m ?? null;
    const capturedAt = value?.captured_at || null;

    /* ── Initial status check ──────────────────────────────────── */
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const res = await livenessService.getStatus();
            if (cancelled) return;
            if (res.success) {
                onChange({
                    verified:    res.verified,
                    verified_at: res.verifiedAt,
                    image_url:   res.imageUrl,
                    lat:         res.lat,
                    lng:         res.lng,
                    accuracy_m:  res.accuracyM,
                    captured_at: res.capturedAt,
                });
            }
            setStatusLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── Submit handler — receives blob + geo from LivenessCapture ── */
    const handleCaptured = async ({ imageBlob, lat: capLat, lng: capLng, accuracyM: capAcc, capturedAt: capWhen }) => {
        setSubmitting(true);
        setServerError(null);
        const res = await livenessService.submit({
            imageBlob,
            lat:        capLat,
            lng:        capLng,
            accuracyM:  capAcc,
            capturedAt: capWhen,
        });
        setSubmitting(false);

        if (res.success) {
            // Refresh from backend so we get the signed image URL the server
            // just generated (instead of trying to embed the local Blob URL).
            const status = await livenessService.getStatus();
            onChange({
                verified:    true,
                verified_at: status.verifiedAt || res.data?.verified_at || new Date().toISOString(),
                image_url:   status.imageUrl || null,
                lat:         status.lat ?? capLat ?? null,
                lng:         status.lng ?? capLng ?? null,
                accuracy_m:  status.accuracyM ?? capAcc ?? null,
                captured_at: status.capturedAt || capWhen || null,
            });
        } else {
            const msg = res.message || 'Could not save your verification. Please retake.';
            setServerError(msg);
            if (onError) onError(msg);
        }
    };

    /* ── Already-verified state ────────────────────────────────── */
    if (statusLoading) {
        return (
            <div className="flex flex-column gap-3 align-items-center py-4">
                <Skeleton width="240px" height="240px" borderRadius="50%" />
                <Skeleton width="14rem" height="1rem" />
            </div>
        );
    }

    if (verified) {
        const formattedDate = verifiedAt
            ? new Date(verifiedAt).toLocaleDateString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
            })
            : null;
        const formattedCaptured = capturedAt
            ? new Date(capturedAt).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit',
            })
            : null;
        const hasLocation = lat !== null && lng !== null;

        return (
            <div className="flex flex-column gap-3 align-items-center py-3">
                {/* Header */}
                <div className="text-center">
                    <h2 className="text-lg font-semibold text-900 m-0 mb-1">Identity verified</h2>
                    <p className="text-600 text-sm m-0">
                        Your live selfie was captured and saved. This is a one-time step.
                    </p>
                </div>

                {/* The actual saved selfie */}
                <div
                    className="border-round-2xl overflow-hidden surface-100"
                    style={{ width: '100%', maxWidth: '280px', aspectRatio: '1 / 1' }}
                >
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt="Verified live selfie"
                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                    ) : (
                        <div className="flex flex-column align-items-center justify-content-center h-full text-500">
                            <i className="pi pi-image pi-2x mb-2" aria-hidden="true" />
                            <small>Saved photo unavailable</small>
                        </div>
                    )}
                </div>

                {/* Location + capture timestamp + verified badge */}
                <div className="surface-50 border-round p-3 w-full" style={{ maxWidth: '320px' }}>
                    <div className="flex flex-column gap-2 text-sm">
                        <div className="flex align-items-center gap-2">
                            <i className="pi pi-shield text-green-600" aria-hidden="true" />
                            <span className="text-900 font-medium">Liveness passed</span>
                            {formattedDate && (
                                <span className="text-500 text-xs ml-auto">{formattedDate}</span>
                            )}
                        </div>

                        {hasLocation ? (
                            <div className="flex align-items-start gap-2">
                                <i className="pi pi-map-marker text-primary mt-1" aria-hidden="true" />
                                <div className="flex flex-column">
                                    <span className="text-700" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {lat.toFixed(5)}, {lng.toFixed(5)}
                                    </span>
                                    {accuracyM ? (
                                        <small className="text-500">±{accuracyM} m accuracy</small>
                                    ) : null}
                                </div>
                            </div>
                        ) : (
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-map-marker text-500" aria-hidden="true" />
                                <span className="text-500 text-xs">Location not recorded</span>
                            </div>
                        )}

                        {formattedCaptured && (
                            <div className="flex align-items-center gap-2">
                                <i className="pi pi-clock text-500" aria-hidden="true" />
                                <small className="text-600">Captured {formattedCaptured}</small>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    /* ── Capture flow ──────────────────────────────────────────── */
    return (
        <div className="flex flex-column gap-3">
            <div>
                <h2 className="text-lg font-semibold text-900 m-0 mb-1">Verify yourself</h2>
                <p className="text-600 text-sm m-0">
                    Look into the camera and blink twice slowly. We&apos;ll capture
                    a single photo with your location to authenticate your profile.
                    This is a one-time step.
                </p>
            </div>

            {serverError && (
                <Message severity="error" text={serverError} className="w-full" />
            )}

            <div
                className="surface-50 border-round p-3 flex flex-column gap-3 align-items-center"
                style={{ minHeight: '320px' }}
            >
                <Suspense
                    fallback={
                        <div className="flex flex-column gap-3 align-items-center py-4">
                            <Skeleton width="240px" height="240px" borderRadius="50%" />
                            <Skeleton width="14rem" height="1rem" />
                            <small className="text-500">Loading face detector…</small>
                        </div>
                    }
                >
                    <LivenessCapture
                        onCaptured={handleCaptured}
                        submitting={submitting || saving}
                    />
                </Suspense>
            </div>

            <div className="surface-100 border-round p-3 flex align-items-start gap-2">
                <i className="pi pi-info-circle text-primary mt-1" aria-hidden="true" />
                <small className="text-600 line-height-3">
                    Your selfie is stored securely with a server-side timestamp and
                    location overlay. We never reuse it for marketing. This step
                    cannot be skipped — your profile won&apos;t be marked complete
                    until it passes.
                </small>
            </div>
        </div>
    );
};

/* Static helpers — mirror the contract used by BasicInfoStep / UserTypeStep
   so ProfileComplete.js can do `LivenessStep.runValidations()` and
   `LivenessStep.hasErrors()` uniformly. */
LivenessStep.runValidations = (value) => {
    const errors = {};
    if (!value || !value.verified) {
        errors.verified = 'Please complete the live-selfie verification before submitting.';
    }
    return errors;
};

LivenessStep.hasErrors = (errors) => Object.values(errors || {}).some((e) => e && e !== '');

export default LivenessStep;
